import type { CityConfig } from "@/pages/city-template/types";

export const gladstoneConfig: CityConfig = {
  city: "Gladstone",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "gladstone",
  route: "/cities/gladstone",
  title: "Emergency Passport Gladstone OR | Expedited Passport Renewal Clackamas County",
  metaDescription: "Emergency passport and expedited passport services in Gladstone, OR — as fast as 24 hours. Same-day courier processing for Gladstone and Clackamas County travelers. A+ BBB rated, registered State Dept. courier. No office visit required.",
  h1: "Emergency & Expedited Passport Services in Gladstone, Oregon",
  heroSubtitle: "Trip booked and your passport expired? Cruise leaving from Seattle in days? Fast Passport Center helps Gladstone residents get their expedited passport fast — as fast as 24 hours. A+ BBB rated. No office visit to our facility required for most services.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Gladstone%20Oregon%20small%20town%20charm%20along%20the%20Clackamas%20River%20with%20lush%20Pacific%20Northwest%20evergreen%20forest%20canopy%2C%20quiet%20residential%20streets%20lined%20with%20mature%20Douglas%20fir%20trees%2C%20soft%20golden%20afternoon%20light%20filtering%20through%20forest%2C%20warm%20amber%20tones%2C%20photorealistic%20Pacific%20Northwest%20landscape%20photography%2C%20serene%20atmosphere%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=gladstone-or-hero-01&orientation=landscape",
  heroImageAlt: "Gladstone Oregon scenic neighborhood along the Clackamas River with Pacific Northwest forest",
  geoPosition: "45.3840;-122.5957",
  geoPlacename: "Gladstone, Oregon",
  lastModified: "2026-06-04",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "300 5th Ave, Suite 600, Seattle, WA 98104",
    distance: "About 175 miles north — 3 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Gladstone", zips: "97027" },
    { name: "Oregon City", zips: "97045" },
    { name: "Milwaukie", zips: "97222, 97267" },
    { name: "West Linn", zips: "97068" },
    { name: "Lake Oswego", zips: "97034–97035" },
    { name: "Clackamas", zips: "97015" },
    { name: "Happy Valley", zips: "97086" },
    { name: "Canby", zips: "97013" },
    { name: "Wilsonville", zips: "97070" },
    { name: "Tualatin", zips: "97062" },
    { name: "Tigard", zips: "97223, 97224" },
    { name: "Portland", zips: "97201–97299" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit to our facility required. Complete your expedited passport order from your Gladstone home in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from Clackamas County", desc: "FedEx and UPS locations throughout Gladstone and Oregon City offer overnight shipping — most are open evenings." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every expedited passport application is reviewed before submission to catch errors that would otherwise cause costly delays." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the Seattle Passport Agency — no hunting for public appointments." },
  ],
  testimonials: [
    {
      name: "Kevin D.",
      location: "Gladstone, OR",
      rating: 5,
      text: "My passport expired three weeks before a business trip to Amsterdam. Fast Passport Center handled everything remotely — I shipped from the Oregon City FedEx and had my renewed passport in 6 days. Unbelievably smooth.",
      service: "Emergency Passport",
    },
    {
      name: "Sandra T.",
      location: "Gladstone, OR",
      rating: 5,
      text: "Our daughter's first passport for a study abroad program in Spain. Fast Passport Center walked us through the child passport process clearly — both parents consenting, the right photos, everything. Passport arrived in 8 days.",
      service: "Child Passport",
    },
    {
      name: "Michael R.",
      location: "Oregon City, OR",
      rating: 5,
      text: "Last-minute cruise deal from Seattle and my passport expired three years ago. Fast Passport Center got me an expedited renewal in 5 business days. Shipped from the McLoughlin Blvd UPS Store. Saved the trip.",
      service: "Expedited Passport Renewal",
    },
  ],
  facilities: [
    {
      id: "gladstone-post",
      name: "Gladstone Post Office",
      type: "USPS Passport Acceptance",
      address: "18905 Portland Ave",
      city: "Gladstone",
      state: "OR",
      zip: "97027",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Downtown Gladstone — primary local acceptance location",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance location in Gladstone. Appointment required — book well in advance through USPS.com. Photo services available nearby.",
      icon: "ri-mail-line",
    },
    {
      id: "oregon-city-post",
      name: "Oregon City Post Office",
      type: "USPS Passport Acceptance",
      address: "610 7th St",
      city: "Oregon City",
      state: "OR",
      zip: "97045",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Oregon City — about 2 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Larger Oregon City post office with faster appointment availability than Gladstone for peak seasons.",
      icon: "ri-mail-line",
    },
    {
      id: "clackamas-county-clerk",
      name: "Clackamas County Clerk — Passport Services",
      type: "County Passport Acceptance",
      address: "1710 Red Soils Ct, Suite 100",
      city: "Oregon City",
      state: "OR",
      zip: "97045",
      phone: "503-655-8551",
      hours: "Mon–Fri 8:30am–4:30pm (by appointment)",
      distance: "Oregon City — County Government Center, 3 miles south",
      website: "https://www.clackamas.us/recording",
      websiteLabel: "Clackamas County Clerk",
      notes: "County clerk passport acceptance — often faster appointment availability than USPS during peak season. Call ahead to confirm.",
      icon: "ri-government-line",
    },
    {
      id: "milwaukie-post",
      name: "Milwaukie Post Office",
      type: "USPS Passport Acceptance",
      address: "11122 SE Oak St",
      city: "Milwaukie",
      state: "OR",
      zip: "97222",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Milwaukie — about 4 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Milwaukie option for northern Gladstone residents near the Portland border.",
      icon: "ri-mail-line",
    },
    {
      id: "lake-oswego-post",
      name: "Lake Oswego Post Office",
      type: "USPS Passport Acceptance",
      address: "4000 Kruse Way Pl",
      city: "Lake Oswego",
      state: "OR",
      zip: "97035",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Lake Oswego — about 6 miles northwest",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Lake Oswego option for Gladstone residents with Lake Oswego area ties.",
      icon: "ri-mail-line",
    },
    {
      id: "west-linn-post",
      name: "West Linn Post Office",
      type: "USPS Passport Acceptance",
      address: "4380 Willamette Falls Dr",
      city: "West Linn",
      state: "OR",
      zip: "97068",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "West Linn — about 4 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "West Linn acceptance option for Gladstone residents on the west Clackamas County corridor.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Gladstone, Oregon?",
      a: "Yes. Through Fast Passport Center, Gladstone travelers can get an emergency passport or expedited passport in as little as 24 hours for urgent travel, or 2–9 business days for standard expedited processing. Start your order online — the sooner you start, the more speed options are available.",
    },
    {
      q: "How fast can I renew my passport from Gladstone, OR?",
      a: "Expedited passport renewal from Gladstone is available in as little as 3 business days through Fast Passport Center. We are a registered U.S. Department of State courier with reserved agency submission slots. No office visit required for most renewals — ship overnight from any FedEx or UPS in the Oregon City or Portland metro area.",
    },
    {
      q: "Where do Gladstone residents go for same-day passport help?",
      a: "Same-day passport appointments at the Seattle Passport Agency are only available for life-or-death emergencies and are not guaranteed. For most Gladstone-area travelers, Fast Passport Center is the faster, more predictable option — no 3-hour drive to Seattle required. We serve Gladstone, Oregon City, Milwaukie, and all of Clackamas County.",
    },
    {
      q: "Does Gladstone, Oregon have a passport office?",
      a: "Gladstone does not have a regional passport agency. The closest is the Seattle Passport Agency in Washington, about 175 miles north. For passport acceptance, Gladstone residents can use the Gladstone Post Office, Oregon City Post Office, or Clackamas County Clerk's office. For expedited processing, Fast Passport Center handles the agency submission remotely.",
    },
    {
      q: "Can I renew my passport from Gladstone without visiting an office?",
      a: "Yes. Most adult Gladstone residents renewing a passport can do so entirely by mail. Start online with Fast Passport Center, complete your documents at home, and ship overnight from FedEx or UPS in Gladstone or Oregon City. No visit to our facility required.",
    },
    {
      q: "What if my passport expired before a trip from Gladstone?",
      a: "Act now — don't wait. If your passport expired and your trip is in 10 weeks or less, standard post office processing almost certainly won't arrive in time. Fast Passport Center can expedite your renewal as fast as 3 business days for most Gladstone-area travelers.",
    },
    {
      q: "How do I get a child passport in Gladstone?",
      a: "Child passports (for minors under 16) require an in-person acceptance step with parental consent — this cannot be skipped. Once the in-person acceptance step is done at the Gladstone or Oregon City Post Office or Clackamas County Clerk's office, Fast Passport Center handles expedited processing. We provide a clear checklist so first-time parents don't miss a step.",
    },
    {
      q: "What airport do Gladstone passport holders usually fly from?",
      a: "Portland International Airport (PDX) is the primary airport for Gladstone travelers, about 16 miles north via I-205. PDX offers direct international flights to Europe, Asia, and Canada. For all international departures from PDX, a valid U.S. passport is required. Don't book without confirming your passport is valid.",
    },
    {
      q: "How fast is standard passport processing in Gladstone?",
      a: "Standard passport processing through the Gladstone or Oregon City Post Office runs 6–13 weeks — and during peak travel season (May through August) it can stretch to 14 weeks or longer. If your trip is sooner than that, you need expedited processing through Fast Passport Center.",
    },
    {
      q: "What's the difference between expedited and emergency passport processing?",
      a: "Expedited passport processing (2–9 business days through Fast Passport Center) is for travelers who have upcoming travel within the next several weeks. Emergency passport processing (as fast as 24 hours) is for travelers with documented travel within the next 72 hours. Both are available to Gladstone residents through Fast Passport Center.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Oregon City", address: "19000 S Beavercreek Rd, Oregon City, OR 97045", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Oregon City / Gladstone area", address: "19000 Molalla Ave, Oregon City, OR 97045", hours: "Daily 7 AM – 10 PM" },
    { name: "FedEx Office — West Linn", address: "19133 Willamette Dr, West Linn, OR 97068", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 8 AM – 8 PM" },
    { name: "Walgreens — Milwaukie", address: "10830 SE Oak St, Milwaukie, OR 97222", hours: "Daily 8 AM – 10 PM" },
    { name: "Walmart — Clackamas", address: "16301 SE 82nd Dr, Clackamas, OR 97015", hours: "Daily 7 AM – 11 PM" },
    { name: "CVS — Lake Oswego", address: "400 First St, Lake Oswego, OR 97034", hours: "Daily 7 AM – 10 PM" },
    { name: "The UPS Store — Lake Oswego", address: "1500 NW Bethany Blvd, Lake Oswego, OR 97034", hours: "Mon–Fri 8 AM – 7 PM" },
    { name: "Walgreens — Happy Valley", address: "16120 SE Happy Valley Town Center Dr, Happy Valley, OR 97086", hours: "Daily 8 AM – 9 PM" },
    { name: "FedEx Office — Portland SE", address: "810 SW Broadway, Portland, OR 97205", hours: "Mon–Fri 7 AM – 10 PM" },
    { name: "Costco — Tualatin", address: "7855 SW Dartmouth St, Tualatin, OR 97062", hours: "Mon–Fri 10 AM – 8:30 PM, Sat–Sun 9:30 AM – 6 PM" },
  ],
  dmvLocations: [
    {
      name: "Oregon City Driver & Motor Vehicle Services",
      address: "1021 S Molalla Ave, Oregon City, OR 97045",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Nearest DMV for Gladstone residents. Driver licenses, REAL ID, ID cards.",
      featured: true,
    },
    {
      name: "Clackamas DMV Office",
      address: "5060 SE Hwy 212, Clackamas, OR 97015",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Clackamas DMV — a second Clackamas County option for Gladstone residents.",
      featured: false,
    },
    {
      name: "Portland DMV — McLoughlin",
      address: "9301 SE McLoughlin Blvd, Portland, OR 97222",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Portland McLoughlin option for northern Gladstone residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Primary airport — about 16 miles north via I-205",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "PDX is the main international departure point for Gladstone travelers, with direct flights to Amsterdam, London, Tokyo, Vancouver BC, and major U.S. hubs. Peak seasons run May–August and December–January. Confirm passport validity before booking international flights.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: true,
    },
    {
      code: "SEA",
      name: "Seattle-Tacoma International Airport",
      distance: "Alternative hub — about 175 miles north",
      address: "17801 International Blvd, SeaTac, WA 98158",
      note: "Some Clackamas County travelers use SEA for additional international flight options. Passport required for all international departures.",
      flightUrl: "https://www.portseattle.org/sea-tac",
      flightLabel: "SEA Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport — Gladstone",
      description: "Flying from PDX in 24–72 hours? Our emergency passport courier service is built for Gladstone and Clackamas County residents with urgent travel — last-minute work trips, military orders, family emergencies.",
      bullets: ["As fast as 24 hours", "Reserved Seattle agency submission", "Live case tracking and dedicated agent"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Expedited Passport Renewal",
      description: "Passport expired? Already have a U.S. passport that needs renewal? Gladstone and Oregon City travelers renew fast — ship from FedEx or UPS overnight and we handle the rest.",
      bullets: ["Fastest renewal option for Gladstone residents", "Ship from local FedEx or UPS", "Professional document review included"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a trip? We guide Gladstone travelers through Form DS-64 and the expedited replacement process so you don't miss your travel date.",
      bullets: ["DS-64 form guidance included", "Expedited replacement timelines", "Document review to avoid rejection"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport — Gladstone Families",
      description: "Minors under 16 require an in-person acceptance step and parental consent. We provide a clear checklist for Gladstone parents and handle expedited processing after the acceptance step.",
      bullets: ["Both parents typically required to consent", "Step-by-step checklist provided", "Expedited processing after acceptance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "First-Time Passport Applications",
      description: "Applying for your first U.S. passport from Gladstone? We guide you through the acceptance clerk process — exactly what to bring, where to go, and what happens next.",
      bullets: ["Step-by-step guidance", "Acceptance clerk location list", "Expedited processing after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Registered State Dept. Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — not a reseller. We submit through reserved agency channels with rates 30–100% lower than FedEx, Staples, and other resellers.",
      bullets: ["State Dept. registered courier", "Best price guarantee", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Services",
      description: "Passport name changes after marriage or divorce, damaged passport replacement, second passports for frequent travelers — all with expedited courier access.",
      bullets: ["Name change after marriage or divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Same-day emergency passport processing for Gladstone, Oregon travelers with urgent departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Gladstone, OR residents needing passports faster than standard government mail timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Gladstone, Oregon residents with a passport issued in the last 15 years at age 16 or older." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement for Gladstone travelers with lost or stolen passports before travel." },
    { name: "Child Passport", description: "Expedited child passport services for Gladstone, Oregon families with minors under 16." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport submission serving Gladstone, Oregon and Clackamas County." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Gladstone residents often assume the Oregon City or Milwaukie post office will be fast enough. Standard processing runs 6–13 weeks — and can stretch to 14+ weeks in summer. If your trip is in under 10 weeks, start expedited processing today." },
    { icon: "ri-camera-off-line", title: "Wrong or Rejected Passport Photo", description: "Photos from drugstores and photo booths frequently get rejected for lighting, expression, or background issues. Fast Passport Center reviews your photo specs before submission to prevent this costly delay." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrectly Signed Forms", description: "One missing signature or one wrong date and your application gets returned — costing you weeks. Our pre-submission document review is included with every expedited order." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Format", description: "Some Oregon residents have only short-form birth certificates, which many states do not accept. Fast Passport Center specifies exactly what you need before you ship." },
    { icon: "ri-calendar-close-line", title: "Assuming Standard Mail Is Fast Enough", description: "If your travel is in under 10 weeks, standard USPS processing out of Gladstone is a gamble. Use Fast Passport Center's expedited courier service instead." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatch Between Passport and Airline Ticket", description: "Your passport name must exactly match your international airline ticket. Middle names, hyphens, and suffixes matter. We review name consistency before submission." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Gladstone travel date. Complete your secure order online in minutes — no office visit required." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We confirm your details and send your personalized document checklist — exactly what to gather, nothing more." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Gladstone Area", description: "Drop off at any FedEx or UPS in the Gladstone, Oregon City, or Portland metro area. Most are open evenings and weekends." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Agency Access", description: "We submit your expedited passport application through our reserved Seattle Passport Agency slots — bypassing the public appointment system." },
    { number: "05", icon: "ri-passport-line", title: "Passport Shipped Directly to You", description: "Your passport is completed within your selected timeframe and shipped directly to your Gladstone address." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins",
        "Life-or-death emergencies only",
        "Must drive to Seattle (3 hrs each way)",
        "Very limited appointment availability",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission — no public line",
        "Predictable turnaround",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates",
        "Best price guarantee",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Document review included",
        "Dedicated agent assigned",
        "Daily status updates",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "Standard mail only — no courier access",
        "6–13 weeks average; 14+ weeks in peak season",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited, often weeks out in Seattle", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit Required", agency: "Must drive to Seattle (3 hours each way)", fpc: "Ship overnight from Gladstone area FedEx or UPS" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed tiers" },
    { feature: "Document Review", agency: "None — errors cause rejection and delays", fpc: "Professional pre-submission review included" },
    { feature: "Courier Access", agency: "Not available to public", fpc: "Reserved daily agency submission slots" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by shutdowns" },
    { feature: "Pricing", agency: "Government fees only — but you drive 6 hrs round trip", fpc: "Best price guarantee — typically 30–100% less than resellers" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Portland", route: "/cities/portland" },
    { name: "Emergency passport help in Oregon City", route: "/cities/oregon-city" },
    { name: "Fast passport renewal in West Linn", route: "/cities/west-linn" },
    { name: "Rush passport services near Milwaukie", route: "/cities/milwaukie" },
    { name: "Expedited passports for Clackamas County travelers", route: "/states/oregon" },
  ],
  fpcOfficeNote: "After placing your order online, we send your personalized checklist and overnight shipping instructions. Ship from any FedEx or UPS in Gladstone, Oregon City, or the Portland metro — most are open evenings. No visit to our facility required for most services.",
  fpcOfficeDistance: "Serving Gladstone and all of Clackamas County remotely",
  serviceAreaIntro: "Whether you are in Gladstone, Oregon City, Milwaukie, West Linn, or anywhere in Clackamas County, Fast Passport Center makes emergency and expedited passport processing simple — start online, ship overnight, and we handle the rest.",
  nearbyCitiesIntro: "We serve all Gladstone and Clackamas County zip codes. Ship overnight from any local FedEx or UPS and your documents reach our processing center the next morning.",
  howItWorksIntro: "From start to finish, we make getting an emergency or expedited passport in Gladstone as simple as possible. Most Gladstone clients never visit an office.",
  mistakesIntro: "These are the most common mistakes that cause rejected applications and delays for Gladstone-area travelers. Fast Passport Center reviews your documents before submission to prevent them.",
  testimonialsIntro: "Real reviews from Gladstone, Oregon City, and Clackamas County travelers who needed their passports fast.",
  faqIntro: "The most common questions Gladstone and Clackamas County travelers ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
  travelTiming: {
    title: "Travel & Passport Timing for Gladstone Residents",
    intro: "Gladstone sits at the heart of Clackamas County — and its residents travel for a distinctive mix of reasons. From PDX international departures to Pacific Northwest summer adventures across the Canadian border, understanding how Gladstone-area families actually travel helps us anticipate when passport urgencies arise.",
    cards: [
      {
        icon: "ri-flight-takeoff-line",
        title: "PDX — Portland International Airport",
        subtitle: "Primary airport — about 16 miles north via I-205",
        description: "Portland International Airport is the default departure point for Gladstone travelers. PDX offers direct international service to Amsterdam, London, Reykjavik, Frankfurt, Tokyo, Vancouver BC, and Mexico City — plus connections to Europe, Asia, and Latin America through major U.S. hubs. Peak international travel from PDX runs June through August and December through January. Many Gladstone families book summer international trips in March and April, then realize their passports are expired or within the six-month validity window.",
        tip: "Many countries require your passport to be valid for six months beyond your return date. A passport that 'technically hasn't expired' may still be rejected at the PDX international gate. Check your expiration before you book — not after.",
      },
      {
        icon: "ri-anchor-line",
        title: "Pacific Northwest Summer & Canada Border Crossings",
        subtitle: "Canada road trips, British Columbia vacations, cruise departures from Seattle",
        description: "Gladstone families are among the most active Pacific Northwest travelers in Clackamas County — summer camping trips to Mt. Hood, drives up to British Columbia, and occasional Seattle cruise departures to Alaska are all common. Canada border crossings require a valid U.S. passport for air travel and are strongly recommended for land crossings as well. Summer cruise deals from Seattle to Alaska frequently trigger last-minute passport renewal needs for families who haven't traveled internationally in years.",
        tip: "Land crossings into Canada technically allow NEXUS cards and enhanced driver licenses — but a U.S. passport is the only universally accepted document. If you're flying into Canada or taking an Alaska cruise, a passport is required.",
      },
      {
        icon: "ri-building-4-line",
        title: "Business Travel from Portland Metro",
        subtitle: "Tech, healthcare, and professional travel through PDX and SEA",
        description: "Clackamas County's growing professional population — including Gladstone residents working in the Portland metro tech and healthcare corridors — travels internationally on short notice. Business trips to Toronto, London, and Tokyo frequently come with 2–3 week lead times. Many professionals assume their passport is valid without checking expiration, then discover the problem on a Sunday night before a Tuesday flight.",
        tip: "Business travelers: set a calendar reminder to check your passport expiration 12 months before it expires. If your passport expires within 6 months, renew now — don't wait for a trip to force the issue.",
      },
      {
        icon: "ri-graduation-cap-line",
        title: "Family Travel & Study Abroad",
        subtitle: "Oregon City School District families, college students, and summer programs",
        description: "Gladstone is served by a mix of Clackamas County schools and has strong ties to nearby universities — Portland State, Lewis & Clark, and the Portland-area community college system. Study abroad programs generate significant first-passport demand in the January–March window. Families with school-age children often discover expired passports when booking spring break international trips or summer European vacations with less than six weeks of lead time.",
        tip: "First-time passport applicants (many college students and children) require an in-person acceptance clerk visit. Fast Passport Center provides a clear checklist and acceptance facility list so you don't show up to the post office missing a required document.",
      },
    ],
  },
  destinations: {
    title: "Where Gladstone Travelers Go — And Why Passports Get Urgent",
    intro: "These are the destinations and travel patterns we see most from Gladstone and Clackamas County clients. Understanding local travel behavior is part of why our document review catches issues before they derail your trip.",
    stats: [
      { stat: "16 mi", label: "to PDX — closest major international airport for Gladstone travelers" },
      { stat: "3–8 Wks", label: "typical notice before an international trip when Gladstone residents contact us" },
      { stat: "Jun–Aug", label: "peak season for emergency passport renewal among Oregon families" },
      { stat: "24 Hrs", label: "fastest emergency passport processing available to Gladstone residents" },
    ],
    destinations: [
      { name: "Canada — British Columbia", context: "Summer road trips to Vancouver, Whistler, and Victoria; Alaska cruise departures from Vancouver; business travel to Vancouver tech hub", urgency: "Summer families book BC trips in April–May then discover expired passports", icon: "ri-map-pin-2-line" },
      { name: "Mexico", context: "Winter beach vacations to Cancún, Cabo, and Puerto Vallarta — popular with Clackamas County families escaping Oregon winters", urgency: "Last-minute winter getaway deals frequently booked with 2–3 week lead time", icon: "ri-map-pin-2-line" },
      { name: "Europe", context: "Summer family trips to the UK, France, Germany, Italy; Lewis & Clark and PSU study abroad programs; professional conferences", urgency: "Study abroad application deadlines fall in December–February; travel often 5–8 months later", icon: "ri-map-pin-2-line" },
      { name: "Japan & Southeast Asia", context: "Business travel through PDX connections; cultural trips for Oregon's growing Asian-American community; teaching abroad programs", urgency: "Teaching English abroad programs require passports 6+ months before departure", icon: "ri-map-pin-2-line" },
      { name: "Alaska — Cruise Departures", context: "Alaska cruises departing from Seattle and Vancouver BC — popular with Clackamas County retirees and families during summer months", urgency: "Last-minute cruise deals booked 2–4 weeks before sailing create passport emergencies", icon: "ri-ship-line" },
      { name: "Caribbean", context: "Cruise departures from LA/Long Beach with stops in the Caribbean; direct flights from PDX to Cancún for Gladstone families", urgency: "Holiday and spring break cruises booked in December–January for March–April sailings", icon: "ri-map-pin-2-line" },
    ],
    crossLinksTitle: "Explore Other Oregon Service Areas Near Gladstone",
    crossLinksDesc: "We serve travelers across the Portland metro and greater Oregon with the same expedited passport expertise.",
    crossLinks: [
      { name: "Oregon City", route: "/cities/oregon-city" },
      { name: "Milwaukie", route: "/cities/milwaukie" },
      { name: "West Linn", route: "/cities/west-linn" },
    ],
  },
};